import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles } from '../shared/DashTheme';
import api from '../../services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function AuthorityReportsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [reviewing, setReviewing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterYear)  params.year  = filterYear;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/higher-authority/authority-reports', { params });
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [filterMonth, filterYear, filterStatus]); // eslint-disable-line

  const handleReview = async () => {
    if (!reviewing) return;
    setSubmitting(true);
    try {
      await api.post(`/higher-authority/authority-reports/${reviewing.report.id}/review`, {
        status: reviewing.status,
        note: reviewing.note,
      });
      setReviewing(null);
      fetchReports();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (st) => {
    if (st === 'reviewed') return { bg: 'rgba(16,185,129,0.2)', color: '#6ee7b7', border: 'rgba(16,185,129,0.4)' };
    if (st === 'acknowledged') return { bg: 'rgba(99,102,241,0.2)', color: '#a5b4fc', border: 'rgba(99,102,241,0.4)' };
    return { bg: 'rgba(245,158,11,0.2)', color: '#fcd34d', border: 'rgba(245,158,11,0.4)' };
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="Authority Reports" subtitle="Monthly reports submitted by authority members"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)} />
          <div style={s.content}>
            <PageHeader icon="🏛️" title="Authority Monthly Reports"
              stats={[
                { label: 'Total', value: reports.length },
                { label: 'Pending', value: reports.filter(r => r.status === 'submitted').length },
                { label: 'Reviewed', value: reports.filter(r => r.status !== 'submitted').length },
              ]}
            />
            {error && <div style={{ ...s.alertErr, marginBottom: 16 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...s.input, width: 130 }}>
                <option value="">All Months</option>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ ...s.input, width: 110 }}>
                <option value="">All Years</option>
                {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...s.input, width: 140 }}>
                <option value="">All Status</option>
                <option value="submitted">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="acknowledged">Acknowledged</option>
              </select>
            </div>

            {!reports.length ? <EmptyState text="No reports found" sub="No authority reports match your filters" /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reports.map(r => {
                  const sc = statusColor(r.status);
                  return (
                    <div key={r.id} style={{ ...s.card, padding: '16px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{r.authority_name}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                            {r.authority_type === 'university' ? 'University Authority' : r.department} · {MONTHS[r.month-1]} {r.year}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                            {r.status.toUpperCase()}
                          </span>
                          {r.status === 'submitted' && (
                            <button className="ur-btn-hover" style={{ ...s.btnPrimary, fontSize: 11, padding: '4px 12px' }}
                              onClick={() => setReviewing({ report: r, note: '', status: 'reviewed' })}>
                              Review
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                        {[['Assigned', r.total_assigned], ['Resolved', r.total_resolved], ['Rejected', r.total_rejected], ['Overdue', r.total_overdue]].map(([l, v]) => (
                          <div key={l} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{v}</div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{l}</div>
                          </div>
                        ))}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{r.avg_resolution_days}d</div>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Avg Days</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{r.summary}</div>
                      {r.reviewer_note && (
                        <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(99,102,241,0.15)', borderRadius: 8, fontSize: 12, color: '#a5b4fc', borderLeft: '3px solid rgba(99,102,241,0.5)' }}>
                          <strong>Note:</strong> {r.reviewer_note}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {reviewing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...s.card, width: 460, padding: 28 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 16 }}>Review Report — {reviewing.report.authority_name}</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 6 }}>Decision</label>
              <select value={reviewing.status} onChange={e => setReviewing(r => ({ ...r, status: e.target.value }))} style={s.input}>
                <option value="reviewed">Reviewed</option>
                <option value="acknowledged">Acknowledged</option>
              </select>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 6 }}>Note (optional)</label>
              <textarea value={reviewing.note} onChange={e => setReviewing(r => ({ ...r, note: e.target.value }))}
                rows={3} style={{ ...s.input, resize: 'vertical' }} placeholder="Add a note for the authority member..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="ur-btn-hover" style={{ ...s.btnPrimary, flex: 1 }} onClick={handleReview} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, flex: 1 }} onClick={() => setReviewing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AuthorityReports() {
  return <ThemeProvider><AuthorityReportsInner /></ThemeProvider>;
}
